import React, { useEffect, useState } from "react";
import axios from "../../api";
import {
  Typography,
  Container,
  Paper,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Divider,
} from "@mui/material";
import {
  AssignmentTurnedIn,
  Send,
  HourglassEmpty,
  Inventory2,
  CheckCircle,
  PendingActions,
  Cancel,
  Payments,
  GppMaybe,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import StatCard from "../common/StatCard";

const ASSET_STATUSES = [
  "InStock",
  "Assigned",
  "Returned",
  "UnderRepair",
  "Retired",
  "Lost",
];

const STATUS_LABELS = {
  InStock: "In Stock",
  Assigned: "Assigned",
  Returned: "Returned",
  UnderRepair: "Under Repair",
  Retired: "Retired",
  Lost: "Lost",
};

const LABEL_TO_STATUS = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([k, v]) => [v, k])
);

const STATUS_CARD_COLORS = {
  InStock: "info",
  Assigned: "primary",
  Returned: "neutral",
  UnderRepair: "warning",
  Retired: "neutral",
  Lost: "error",
};

// Ensure every inventory status appears, even when count is 0.
const padStatusRows = (rows) => {
  const map = new Map((rows || []).map((r) => [r._id, r.count || 0]));
  return ASSET_STATUSES.map((id) => ({
    _id: STATUS_LABELS[id] || id,
    count: map.get(id) || 0,
  }));
};

const BreakdownTable = ({ title, rows, onRowClick }) => {
  const total = (rows || []).reduce((s, r) => s + (r.count || 0), 0) || 1;
  return (
    <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell align="right">Count</TableCell>
            <TableCell align="right">Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).map((r, i) => (
            <TableRow
              key={i}
              hover={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              sx={onRowClick ? { cursor: "pointer" } : undefined}
            >
              <TableCell>{r._id || "Unspecified"}</TableCell>
              <TableCell align="right">{r.count}</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(r.count / total) * 100}
                    sx={{ flexGrow: 1 }}
                  />
                  {Math.round((r.count / total) * 100)}%
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {(!rows || rows.length === 0) && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No data
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};

function Analytics({ onLogout }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const [undertakingStats, setUndertakingStats] = useState(null);

  useEffect(() => {
    axios
      .get("/undertaking/analytics")
      .then((r) => setData(r.data.result))
      .catch((e) => console.error(e));
    axios
      .get("/assets/stats")
      .then((r) => setAssetStats(r.data.result))
      .catch((e) => console.error(e));
    axios
      .get("/undertaking/stats")
      .then((r) => setUndertakingStats(r.data))
      .catch((e) => console.error(e));
  }, []);

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const reviewCounts = (data?.byReviewStatus || []).reduce((acc, r) => {
    acc[r._id || "Submitted"] = r.count;
    return acc;
  }, {});

  const statusRows = padStatusRows(assetStats?.byStatus);
  const goToStatus = (statusKey) => {
    if (statusKey) navigate(`/assets?status=${encodeURIComponent(statusKey)}`);
  };

  return (
    <AdminLayout
      title="Analytics"
      subtitle="Undertakings & asset insights — click a KPI to open the related list"
      onLogout={handleLogout}
    >
        <Container maxWidth="lg">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Total Submissions"
                value={undertakingStats?.totalSubmissions}
                icon={<AssignmentTurnedIn />}
                color="info"
                onClick={() => navigate("/dashboard")}
                helper="Open dashboard"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Total Invites"
                value={undertakingStats?.totalInvites}
                icon={<Send />}
                color="primary"
                onClick={() => navigate("/dashboard")}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Pending Invites"
                value={undertakingStats?.totalPending}
                icon={<HourglassEmpty />}
                color="warning"
                onClick={() => navigate("/dashboard")}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Total Assets"
                value={assetStats?.total}
                icon={<Inventory2 />}
                color="secondary"
                onClick={() => navigate("/assets")}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Approved"
                value={reviewCounts.Approved || 0}
                helper="Clean acknowledgments"
                icon={<CheckCircle />}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Awaiting Review"
                value={data?.awaitingCorrections ?? 0}
                helper="Open Corrections"
                icon={<PendingActions />}
                color="warning"
                onClick={() => navigate("/reviews")}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Rejected"
                value={reviewCounts.Rejected || 0}
                icon={<Cancel />}
                color="error"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Warranty Expiring"
                value={assetStats?.warrantyExpiringSoon || 0}
                helper="Open warranty report"
                icon={<GppMaybe />}
                color="warning"
                onClick={() => navigate("/warranty")}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {ASSET_STATUSES.map((statusKey) => {
              const row = statusRows.find((r) => r._id === (STATUS_LABELS[statusKey] || statusKey));
              return (
                <Grid item xs={6} sm={4} md={2} key={statusKey}>
                  <StatCard
                    label={STATUS_LABELS[statusKey] || statusKey}
                    value={row?.count || 0}
                    icon={<Inventory2 />}
                    color={STATUS_CARD_COLORS[statusKey] || "neutral"}
                    onClick={() => goToStatus(statusKey)}
                    helper="Open in Inventory"
                  />
                </Grid>
              );
            })}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <StatCard
                label="Total Purchase Cost"
                value={(assetStats?.totalPurchaseCost || 0).toLocaleString()}
                helper="Sum of purchase cost"
                icon={<Payments />}
                color="success"
                onClick={() => navigate("/assets")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                label="Est. Book Value"
                value={(assetStats?.totalBookValue || 0).toLocaleString()}
                helper={`${assetStats?.bookValueAssetCount || 0} asset(s) with depreciation`}
                icon={<Payments />}
                color="info"
                onClick={() => navigate("/assets")}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <BreakdownTable title="By Department" rows={data?.byDepartment} />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownTable title="Top Manufacturers" rows={data?.topManufacturers} />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownTable
                title="Assets by Status"
                rows={statusRows}
                onRowClick={(r) => goToStatus(LABEL_TO_STATUS[r._id])}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownTable
                title="Assets by Site"
                rows={assetStats?.bySite}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownTable
                title="Assets by Condition"
                rows={assetStats?.byCondition}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownTable
                title="Assets by Location"
                rows={assetStats?.byLocation}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownTable
                title="Submissions by Month"
                rows={data?.submissionsOverTime}
              />
            </Grid>
          </Grid>
        </Container>
    </AdminLayout>
  );
}

export default Analytics;
